// ReportView shared rendering component for the full 12-section president-ready
// post-match analysis report. Used by both AnalysisReport (authenticated) and
// SharedReport (public read-only link).
//
// CRITICAL rules enforced here:
//   - Never render [object Object] every value is passed through safeStr
//   - Every field checks both camelCase and snake_case via pick()
//   - decisive_moments can be array of objects OR legacy strings
//   - Empty arrays show 'No data yet' placeholder, never crash
import React, { useEffect, useState } from 'react';
import {
  Play, AlertTriangle, CheckCircle2, TrendingDown, Target, Shield,
  Dumbbell, BookOpen, Lightbulb, Clock, Award, Activity, Download
} from 'lucide-react';
import { api } from '../utils/api';
import '../reportPrint.css';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Pick the first non-null/non-empty value from obj using any of the given keys. */
function pick(obj, ...keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) {
    const v = obj[k];
    if (v !== null && v !== undefined && v !== '') return v;
  }
  return null;
}

/** Safely render any value as a string never [object Object]. */
function safeStr(v) {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') {
    const cleaned = v.replace(/[\u2013\u2014]/g, '-').replace(/\b(?:Not specified|Not provided)\b/gi, '').replace(/\s{2,}/g, ' ').trim();
    return cleaned;
  }
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) {
    if (v.length === 0) return '';
    return v.map((i) => safeStr(i)).filter(Boolean).join(', ');
  }
  if (typeof v === 'object') {
    // Known safe single-string extractions only — never flatten unknown objects.
    if (v.name) return safeStr(v.name);
    if (v.title) return safeStr(v.title);
    if (v.label) return safeStr(v.label);
    if (v.text) return safeStr(v.text);
    if (v.value) return safeStr(v.value);
    // Unknown object: return empty. Never flatten with Object.values().
    return '';
  }
  return '';
}

function timestampToSeconds(ts) {
  if (!ts) return null;
  const parts = String(ts).split(':').map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return null;
}

function buildVideoUrl(baseUrl, timestamp) {
  if (!baseUrl) return null;
  const isYT = baseUrl.includes('youtube.com') || baseUrl.includes('youtu.be');
  const isVimeo = baseUrl.includes('vimeo.com');
  
  // If no timestamp provided, return base URL as-is (preserve original t= if present)
  if (!timestamp) return baseUrl;
  
  const secs = timestampToSeconds(timestamp);
  
  if (isYT) {
    try {
      // Parse properly so we don't double-add query params
      const url = new URL(baseUrl);
      if (secs !== null) url.searchParams.set('t', `${secs}s`);
      return url.toString();
    } catch {
      return secs !== null ? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}t=${secs}s` : baseUrl;
    }
  }
  if (isVimeo && secs !== null) {
    return `${baseUrl.split('#')[0]}#t=${secs}s`;
  }
  return baseUrl;
}

function normalise(field) {
  if (field == null) return { kind: 'empty', value: null };
  if (typeof field === 'string') return { kind: 'string', value: field.trim() };
  if (Array.isArray(field)) {
    if (field.length === 0) return { kind: 'empty', value: null };
    if (field.every((i) => typeof i === 'string')) return { kind: 'list', value: field };
    return { kind: 'objects', value: field };
  }
  if (typeof field === 'object') return { kind: 'object', value: field };
  return { kind: 'empty', value: null };
}

// Positive and Risk moment type sets (must match engine.js)
const POSITIVE_TYPES = new Set([
  'Goal scored', 'Goal for', 'Big chance created', 'Key chance',
  'Pressing success', 'Counterattack', 'Set-piece attack', 'Attacking transition'
]);
const RISK_TYPES = new Set([
  'Goal against', 'Big chance conceded', 'Build-up error', 'Turnover', 'Press broken',
  'Set-piece defense', 'Defensive organization', 'Defensive mistake',
  'Game-state management issue', 'Substitution concern', 'Fatigue concern',
  'Player role issue', 'Momentum shift', 'Transition moment'
]);

function isPositiveMoment(m) {
  if (typeof m === 'string') return false;
  const cat = pick(m, 'category');
  if (cat && safeStr(cat).toLowerCase() === 'positive') return true;
  const mt = safeStr(pick(m, 'momentType', 'moment_type')).toLowerCase();
  if (!mt) return false;
  for (const t of POSITIVE_TYPES) { if (mt === t.toLowerCase()) return true; }
  if (mt.includes('goal for') || mt.includes('goal scored')) return true;
  return false;
}

function isRiskMoment(m) {
  if (typeof m === 'string') return false;
  const cat = pick(m, 'category');
  if (cat && safeStr(cat).toLowerCase() === 'risk') return true;
  const mt = safeStr(pick(m, 'momentType', 'moment_type')).toLowerCase();
  if (!mt) return false;
  for (const t of RISK_TYPES) { if (mt === t.toLowerCase()) return true; }
  if (mt.includes('against') || mt.includes('mistake') || mt.includes('error') ||
      mt.includes('turnover') || mt.includes('conceded') || mt.includes('broken')) return true;
  return false;
}

// Convert impact string to numeric score for sorting (higher = more important)
function structuredScoreValue(scoreObj) {
  if (!scoreObj) return 0;
  if (typeof scoreObj === 'number') return scoreObj;
  if (typeof scoreObj === 'object') return Number(scoreObj.score || 0);
  return 0;
}

function impactScore(m) {
  if (typeof m === 'string') return 0;
  const positiveScore = structuredScoreValue(pick(m, 'positiveImpactScore', 'positive_impact_score'));
  const riskScore = structuredScoreValue(pick(m, 'riskScore', 'risk_score'));
  if (positiveScore || riskScore) return Math.max(positiveScore, riskScore) * 10;
  const impact = safeStr(pick(m, 'impact', 'impactLevel')).toLowerCase();
  if (impact.includes('match') || impact.includes('decisive')) return 100;
  if (impact.includes('high')) return 75;
  if (impact.includes('medium') || impact.includes('mid')) return 50;
  if (impact.includes('low')) return 25;
  return 10;
}

// ─────────────────────────────────────────────────────────────────────────────
// Small UI primitives
// ─────────────────────────────────────────────────────────────────────────────

function Badge({ text, color }) {
  const colors = {
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    navy: 'bg-navy-50 text-navy-700 border-navy-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200',
  };
  const resolvedColor = color || 'gray';
  return (
    <span className={`inline-flex items-center text-xs font-semibold px-2.5 py-0.5 rounded-full border ${colors[resolvedColor] || colors.gray}`}>
      {safeStr(text)}
    </span>
  );
}

function impactColor(impact) {
  if (!impact) return 'gray';
  const i = safeStr(impact).toLowerCase();
  if (i.includes('match') || i.includes('high') || i.includes('decisive')) return 'red';
  if (i.includes('medium') || i.includes('mid')) return 'amber';
  return 'green';
}

function qualityColor(label) {
  if (!label) return 'gray';
  const l = safeStr(label).toLowerCase();
  if (l === 'excellent') return 'green';
  if (l === 'good') return 'blue';
  if (l === 'basic' || l === 'limited') return 'amber';
  if (l === 'minimal') return 'red';
  return 'gray';
}

function severityColor(sev) {
  if (!sev) return 'gray';
  const s = safeStr(sev).toLowerCase();
  if (s === 'high') return 'red';
  if (s === 'medium') return 'amber';
  return 'green';
}


function evidenceStatusColor(status) {
  if (!status) return 'gray';
  const s = safeStr(status).toLowerCase();
  if (s.includes('provided') || s.includes('timestamp')) return 'green';
  if (s.includes('minute')) return 'amber';
  return 'red';
}

function WatchBtn({ baseUrl, timestamp }) {
  const url = buildVideoUrl(baseUrl, timestamp);
  if (!timestamp) return null;
  return (
    <>
      {/* Screen: clickable link */}
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="screen-only inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 underline ml-2 transition-colors"
        >
          <Play className="w-3 h-3" /> Watch at {timestamp}
        </a>
      ) : (
        <span className="text-gray-400 text-xs ml-2">Video reference: {timestamp}</span>
      )}
      {/* Print: show as plain text */}
      <span className="print-only text-gray-600 text-xs ml-2">Video reference: {timestamp}</span>
    </>
  );
}

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 px-6 py-4 border-b border-gray-100">
      <div className="w-8 h-8 rounded-lg bg-navy-50 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Icon className="w-4 h-4 text-navy-700" />
      </div>
      <div>
        <h2 className="text-navy-900 font-bold text-base">{safeStr(title)}</h2>
        {subtitle && <p className="text-gray-400 text-xs mt-0.5">{safeStr(subtitle)}</p>}
      </div>
    </div>
  );
}

function Card({ children, className = '' }) {
  return (
    <div className={`report-card section-card bg-white rounded-2xl border border-gray-200 overflow-hidden mb-5 ${className}`}>
      {children}
    </div>
  );
}

// Renders a labelled field block only if the value is non-empty
function FieldBlock({ label, value, className = '' }) {
  const text = safeStr(value);
  if (!text) return null;
  return (
    <div className={className}>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">{safeStr(label)}</p>
      <p className="text-gray-700 text-sm leading-relaxed">{text}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Decisive Moment Card one per moment, handles objects and strings
// ─────────────────────────────────────────────────────────────────────────────

function MomentCard({ moment, index, externalVideoUrl }) {
  if (typeof moment === 'string') {
    return (
      <div className="moment-card border border-gray-200 rounded-xl p-5 mb-3">
        <p className="text-gray-700 text-sm leading-relaxed">{safeStr(moment)}</p>
      </div>
    );
  }

  const minute = pick(moment, 'minute');
  const momentType = pick(moment, 'momentType', 'moment_type');
  const phase = pick(moment, 'phaseOfPlay', 'phase_of_play', 'phase');
  const impact = pick(moment, 'impact', 'impactLevel');
  const whatHappened = pick(moment, 'whatHappened', 'what_happened', 'event');
  const whyItMattered = pick(moment, 'whyItMattered', 'why_it_mattered', 'evidenceNotes', 'evidence_notes');
  const coachObs = pick(moment, 'coachObservation', 'coach_observation');
  const videoReview = pick(moment, 'videoReview', 'video_review', 'reviewInstruction', 'review_instruction');
  const trainingImpl = pick(moment, 'trainingImplication', 'training_implication');
  const evidenceStatus = pick(moment, 'evidenceStatus', 'evidence_status');
  const videoTs = pick(moment, 'videoTimestamp', 'video_timestamp');
  const tags = pick(moment, 'tags');
  const confidence = pick(moment, 'confidence');
  const failureResponsibility = pick(moment, 'failureResponsibility', 'failure_responsibility');
  const causeEffect = pick(moment, 'causeEffect', 'cause_effect');
  const positiveImpactScore = pick(moment, 'positiveImpactScore', 'positive_impact_score');
  const riskScore = pick(moment, 'riskScore', 'risk_score');

  const isPos = isPositiveMoment(moment);
  const isRisk = isRiskMoment(moment);
  const borderColor = isPos ? 'border-l-emerald-400' : isRisk ? 'border-l-red-400' : 'border-l-navy-300';

  return (
    <div className={`border border-gray-200 border-l-4 ${borderColor} rounded-xl p-5 mb-4 bg-white`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-2xl font-black text-navy-900 tabular-nums">
            {minute ? `${safeStr(minute)}'` : `#${index + 1}`}
          </span>
          <div>
            <p className="text-navy-900 font-bold text-sm leading-tight">
              {safeStr(momentType) || 'Key Moment'}
            </p>
            {/* Role label — derived from matchState conclusion, never from whatHappened */}
            {(() => {
              const roleRaw = safeStr(pick(moment, 'matchStateRole', 'role', 'conclusionRole')).toLowerCase();
              const roleLabels = {
                opening_goal: 'Opening Goal',
                equalizer: 'Equalizer',
                temporary_equalizer: 'Positive Equalizer (Temporary)',
                lead_taking: 'Lead-Taking Goal',
                lead_extending: 'Lead-Extending Goal',
                comeback_lead_or_equalizer: 'Comeback Goal',
                final_lead_change: 'Decisive Lead Change',
                result_sealing: 'Result-Sealing Goal',
              };
              const label = roleLabels[roleRaw];
              if (!label) return null;
              const isPositiveRole = ['opening_goal','equalizer','temporary_equalizer','lead_taking','lead_extending','comeback_lead_or_equalizer'].includes(roleRaw);
              const colorClass = isPositiveRole ? 'text-emerald-600' : 'text-red-600';
              return <p className={`text-xs font-semibold mt-0.5 ${colorClass}`}>{label}</p>;
            })()}
            {phase && <p className="text-gray-400 text-xs mt-0.5">Phase: {safeStr(phase)}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
          {impact && <Badge text={impact} color={impactColor(impact)} />}
          {isPos && structuredScoreValue(positiveImpactScore) > 0 && <Badge text={`Positive Impact ${structuredScoreValue(positiveImpactScore).toFixed(1)}/10`} color="green" />}
          {isRisk && structuredScoreValue(riskScore) > 0 && <Badge text={`Risk ${structuredScoreValue(riskScore).toFixed(1)}/10`} color="red" />}
          {momentType && <Badge text={momentType} color="navy" />}
          {evidenceStatus && <Badge text={evidenceStatus} color={evidenceStatusColor(evidenceStatus)} />}
        </div>
      </div>

      {whatHappened && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">What Happened</p>
          <p className="text-gray-700 text-sm leading-relaxed">{safeStr(whatHappened)}</p>
        </div>
      )}

      {coachObs && (
        <div className="bg-gray-50 rounded-lg px-4 py-3 mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Coach Observation</p>
          <p className="text-gray-700 text-sm leading-relaxed">{safeStr(coachObs)}</p>
        </div>
      )}

      {whyItMattered && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Why It Mattered</p>
          <p className="text-gray-700 text-sm leading-relaxed">{safeStr(whyItMattered)}</p>
        </div>
      )}

      {confidence && (
        <div className="bg-navy-50 border border-navy-100 rounded-lg px-4 py-3 mb-3">
          <p className="text-xs font-semibold text-navy-700 uppercase tracking-wide mb-1">Confidence: {safeStr(confidence.level || confidence)}</p>
          {Array.isArray(confidence.why) && <p className="text-navy-800 text-sm leading-relaxed">{confidence.why.map((w) => safeStr(w)).join(' · ')}</p>}
        </div>
      )}

      {failureResponsibility &&
        failureResponsibility.primaryResponsibility !== 'Insufficient evidence' &&
        !(failureResponsibility.statement && failureResponsibility.statement.startsWith('Responsibility cannot')) &&
        !(failureResponsibility.statement && failureResponsibility.statement.startsWith('Cause requires video')) && (
        <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 mb-3">
          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">Failure Responsibility Classification</p>
          {failureResponsibility.statement ? (
            <p className="text-amber-900 text-sm leading-relaxed">{safeStr(failureResponsibility.statement)}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <FieldBlock label="Primary Responsibility" value={failureResponsibility.primaryResponsibility} />
              <FieldBlock label="Contributing Factor" value={failureResponsibility.contributingFactor} />
              <FieldBlock label="Confidence" value={failureResponsibility.confidence} />
              <FieldBlock label="Correctable During Match" value={failureResponsibility.correctableDuringMatch} />
              <FieldBlock label="Required Intervention" value={failureResponsibility.requiredIntervention} />
              <FieldBlock label="Evidence Used" value={Array.isArray(failureResponsibility.evidenceUsed) ? failureResponsibility.evidenceUsed.join(' · ') : failureResponsibility.evidenceUsed} />
            </div>
          )}
        </div>
      )}

      {causeEffect && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
          <FieldBlock label="Why" value={causeEffect.why} />
          <FieldBlock label="Immediate Consequence" value={causeEffect.immediateConsequence} />
          <FieldBlock label="Long-term Consequence" value={causeEffect.longTermConsequence} />
          <FieldBlock label="Expected Outcome" value={causeEffect.expectedOutcome} />
        </div>
      )}

      {trainingImpl && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3 mb-3">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Training Implication</p>
          <p className="text-emerald-800 text-sm leading-relaxed">{safeStr(trainingImpl)}</p>
        </div>
      )}

      {videoTs && (
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs font-mono text-gray-600">{safeStr(videoTs)}</span>
          {externalVideoUrl && <WatchBtn baseUrl={externalVideoUrl} timestamp={videoTs} />}
        </div>
      )}

      {videoReview && (
        <div className="border-t border-gray-100 pt-3 mt-1">
          <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-1">Staff Review Instruction</p>
          <p className="text-gray-600 text-sm italic leading-relaxed">{safeStr(videoReview)}</p>
        </div>
      )}

      {tags && (
        <div className="flex gap-1 flex-wrap mt-3">
          {(Array.isArray(tags) ? tags : safeStr(tags).split(',').map((t) => t.trim()))
            .filter(Boolean).map((tag, i) => (
            <span key={i} className="text-xs bg-navy-50 text-navy-700 rounded-full px-2 py-0.5 border border-navy-100">
              {safeStr(tag)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hypothesis Card careful language
// ─────────────────────────────────────────────────────────────────────────────

function HypothesisCard({ h, index, externalVideoUrl }) {
  if (typeof h === 'string') {
    return (
      <div className="moment-card border border-gray-200 rounded-xl p-5 mb-3">
        <p className="text-gray-700 text-sm leading-relaxed">{safeStr(h)}</p>
      </div>
    );
  }
  const finding = pick(h, 'finding', 'hypothesis');
  const staffImpl = pick(h, 'staffImplication', 'staff_implication');
  const reviewInst = pick(h, 'reviewInstruction', 'review_instruction');
  const evidence = pick(h, 'evidence');

  return (
    <div className="moment-card border border-gray-200 rounded-xl p-5 mb-4">
      <div className="flex items-start gap-2 mb-3">
        <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
        <p className="text-navy-900 font-bold text-sm leading-snug">
          {safeStr(finding) || `Hypothesis ${index + 1}`}
        </p>
      </div>
      <p className="text-xs text-amber-600 font-medium mb-3 italic">
        Hypothesis. Requires video confirmation before being treated as a conclusion.
      </p>

      {staffImpl && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-3">
          <p className="text-xs font-semibold text-blue-600 uppercase tracking-wide mb-1">Possible Cause: What This Means for Staff</p>
          <p className="text-blue-800 text-sm leading-relaxed">{safeStr(staffImpl)}</p>
        </div>
      )}

      {evidence && Array.isArray(evidence) && evidence.length > 0 && (
        <div className="mb-3">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Evidence</p>
          <ul className="space-y-2">
            {evidence.map((ev, j) => {
              if (typeof ev === 'string') {
                return <li key={j} className="flex gap-2 text-sm text-gray-700"><span className="text-navy-400 mt-0.5">•</span>{safeStr(ev)}</li>;
              }
              const evTs = pick(ev, 'timestamp', 'videoTimestamp', 'video_timestamp');
              return (
                <li key={j} className="flex items-start gap-2 text-sm">
                  <span className="text-navy-400 mt-1 flex-shrink-0">•</span>
                  <span className="text-gray-700">
                    {pick(ev, 'type') && <span className="font-semibold text-navy-800">{safeStr(pick(ev, 'type'))}: </span>}
                    {safeStr(pick(ev, 'description'))}
                    {pick(ev, 'minute') && <span className="text-gray-400">: min {safeStr(pick(ev, 'minute'))}</span>}
                    {evTs && (
                      <span className="inline-flex items-center gap-1 ml-1">
                        <span className="text-gray-400 font-mono text-xs">[{safeStr(evTs)}]</span>
                        {externalVideoUrl && <WatchBtn baseUrl={externalVideoUrl} timestamp={evTs} />}
                      </span>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {reviewInst && (
        <div className="border-t border-gray-100 pt-3">
          <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-1">Video Review Scope</p>
          <p className="text-gray-600 text-sm italic">{safeStr(reviewInst)}</p>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mistake Attribution Card
// ─────────────────────────────────────────────────────────────────────────────

function MistakeCard({ m, index, externalVideoUrl }) {
  if (typeof m === 'string') {
    return <div className="flex gap-2 text-sm text-gray-700 py-1"><span className="text-red-400 mt-0.5">•</span>{safeStr(m)}</div>;
  }
  const player = pick(m, 'player');
  const role = pick(m, 'role');
  const momentType = pick(m, 'momentType', 'moment_type');
  const minute = pick(m, 'minute');
  const moment = pick(m, 'moment', 'whatHappened', 'what_happened');
  const errorType = pick(m, 'errorType', 'error_type');
  const primaryUnit = pick(m, 'primaryUnit', 'primary_unit');
  const riskLevel = pick(m, 'riskLevel', 'risk_level');
  const trainingFocusArea = pick(m, 'trainingFocusArea', 'training_focus_area');
  const coachNote = pick(m, 'coachNote', 'coach_note', 'coachObservation', 'coach_observation');
  const videoTs = pick(m, 'videoTimestamp', 'video_timestamp');

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          {(player || role) && <p className="text-navy-900 font-bold text-sm">{safeStr(player) || safeStr(role)}</p>}
          {role && <p className="text-gray-400 text-xs">{safeStr(role)}</p>}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {minute && <span className="text-navy-900 font-black text-lg tabular-nums">{safeStr(minute)}'</span>}
          {riskLevel && <Badge text={`${safeStr(riskLevel)} risk`} color={severityColor(riskLevel)} />}
        </div>
      </div>
      {momentType && <p className="text-gray-500 text-xs mb-2 uppercase tracking-wide font-semibold">{safeStr(momentType)}</p>}
      {moment && <p className="text-gray-700 text-sm mb-3 leading-relaxed">{safeStr(moment)}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        {errorType && <FieldBlock label="Error Type" value={errorType} />}
        {primaryUnit && <FieldBlock label="Primary Unit" value={primaryUnit} />}
        {trainingFocusArea && <FieldBlock label="Training Focus Area" value={trainingFocusArea} className="sm:col-span-2" />}
      </div>
      {coachNote && (
        <div className="bg-gray-50 rounded-lg px-3 py-2 mb-2">
          <p className="text-xs font-semibold text-gray-400 mb-0.5">Coach Note</p>
          <p className="text-gray-700 text-sm">{safeStr(coachNote)}</p>
        </div>
      )}
      {videoTs && externalVideoUrl && <WatchBtn baseUrl={externalVideoUrl} timestamp={videoTs} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Coaching Decision Card
// ─────────────────────────────────────────────────────────────────────────────

function DecisionCard({ c, index }) {
  if (typeof c === 'string') return <p className="text-gray-700 text-sm">{safeStr(c)}</p>;
  const decision = pick(c, 'decision');
  const minute = pick(c, 'minute');
  const outcome = pick(c, 'outcome');
  const alt = pick(c, 'alternativeConsidered', 'alternative_considered');

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <p className="text-navy-900 font-bold text-sm">{safeStr(decision) || `Decision ${index + 1}`}</p>
        {minute && <span className="text-navy-900 font-black text-base tabular-nums">{safeStr(minute)}</span>}
      </div>
      {outcome && (
        <div className="flex gap-4 text-sm flex-wrap">
          <div>
            <p className="text-xs font-semibold text-gray-400 mb-0.5">Outcome</p>
            <p className="text-gray-700">{safeStr(outcome)}</p>
          </div>
          {alt && (
            <div>
              <p className="text-xs font-semibold text-gray-400 mb-0.5">Alternative Considered</p>
              <p className="text-gray-700">{safeStr(alt)}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Training Focus Card
// ─────────────────────────────────────────────────────────────────────────────

function TrainingCard({ t, index, showActions, onStatusChange }) {
  if (typeof t === 'string') {
    return <div className="border border-gray-200 rounded-xl p-4 mb-3"><p className="text-gray-700 text-sm">{safeStr(t)}</p></div>;
  }
  const area = pick(t, 'area', 'title');
  const priority = pick(t, 'priority');
  const drill = pick(t, 'drill', 'detail');
  const unit = pick(t, 'unit', 'teamUnit', 'team_unit');
  const whyItMatters = pick(t, 'whyItMatters', 'why_it_matters');
  const linkedMoments = pick(t, 'linkedMoments', 'linked_moments');
  const difficulty = pick(t, 'difficulty');
  const expectedImpact = pick(t, 'expectedImpact', 'expected_impact');
  const estimatedTime = pick(t, 'estimatedTimeToImprove', 'estimated_time_to_improve');
  const status = pick(t, 'status') || 'Not started';
  const priorityColors = { Critical: 'red', High: 'red', Medium: 'amber', Low: 'green' };

  return (
    <div className="moment-card border border-gray-200 rounded-xl p-5 mb-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-navy-900 font-bold text-sm">{safeStr(area) || 'Training Area'}</p>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {priority && <Badge text={`Priority: ${safeStr(priority)}`} color={priorityColors[safeStr(priority)] || 'gray'} />}
          {showActions ? (
            <select className="print-hidden border border-gray-200 rounded-lg px-2 py-1 text-xs text-gray-700" value={safeStr(status)} onChange={(e) => onStatusChange(index, e.target.value)}>
              {['Completed', 'In progress', 'Not started', 'Deferred'].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : <Badge text={safeStr(status)} color="gray" />}
        </div>
      </div>
      {drill && (
        <div className="bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3 mb-2">
          <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-1">Recommended Drill</p>
          <p className="text-emerald-800 text-sm leading-relaxed">{safeStr(drill)}</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
        {unit && <FieldBlock label="Unit" value={unit} />}
        {whyItMatters && <FieldBlock label="Why It Matters" value={whyItMatters} />}
        {difficulty && <FieldBlock label="Difficulty" value={difficulty} />}
        {expectedImpact && <FieldBlock label="Expected Impact" value={expectedImpact} />}
        {estimatedTime && !String(estimatedTime).toLowerCase().includes('confirm') && !String(estimatedTime).toLowerCase().includes('video review') && (
          <FieldBlock label="Estimated Time To Improve" value={estimatedTime} />
        )}
      </div>
      {linkedMoments && Array.isArray(linkedMoments) && linkedMoments.length > 0 && (
        <p className="text-gray-400 text-xs">Linked to: {linkedMoments.map((lm) => safeStr(lm)).join(', ')}</p>
      )}
    </div>
  );
}


function CoachingIntelligenceSection({ intelligence, showActions, reportId }) {
  const summary = Array.isArray(intelligence?.coachingDecisionSummary)
    ? intelligence.coachingDecisionSummary
    : [];
  const findings = Array.isArray(intelligence?.structuredFindings)
    ? intelligence.structuredFindings
    : [];
  const facts = Array.isArray(intelligence?.confirmedFacts) ? intelligence.confirmedFacts : [];
  const reviewQs = Array.isArray(intelligence?.reviewQuestions) ? intelligence.reviewQuestions : [];
  const verification = Array.isArray(intelligence?.laterMatchVerification)
    ? intelligence.laterMatchVerification
    : [];
  const eq = intelligence?.evidenceQuality || {};
  const mechanism = intelligence?.mechanismReview;
  const [busyId, setBusyId] = useState('');
  const [localFindings, setLocalFindings] = useState(findings);
  const findingsKey = JSON.stringify(findings.map((f) => f.id));
  useEffect(() => {
    setLocalFindings(findings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [findingsKey]);

  if (!intelligence || typeof intelligence !== 'object') return null;

  const confirmFinding = async (finding, staffStatus) => {
    if (!showActions || !reportId || !finding?.id) return;
    setBusyId(finding.id);
    try {
      // Prefer persisted finding id when available via API list
      const listed = await api.getCoachingFindings({ report_id: reportId });
      const row = (listed.findings || []).find(
        (f) => f.taxonomyKey === finding.taxonomyKey || f.title === finding.title
      );
      if (row?.id) {
        await api.updateCoachingFinding({ id: row.id, staffStatus, createDecision: staffStatus === 'confirmed' });
      }
      setLocalFindings((prev) => prev.map((f) => (
        f.id === finding.id ? { ...f, staffStatus } : f
      )));
    } catch { /* non-blocking */ }
    setBusyId('');
  };

  return (
    <Card>
      <SectionHeader
        icon={Target}
        title="Coaching Decision Summary"
        subtitle="Evidence-aware priorities for staff confirmation — not automatic tactical verdicts"
      />
      <div className="px-6 py-5 space-y-4">
        <div className="flex flex-wrap gap-3 text-xs">
          <Badge text={`Evidence quality ${safeStr(eq.score)}/100 (${safeStr(eq.label || eq.depth)})`} color="navy" />
          {eq.depth === 'low' && <Badge text="Low evidence — review only" color="amber" />}
        </div>

        {summary.length > 0 && (
          <div className="space-y-3">
            {summary.map((item, index) => (
              <div key={index} className="border border-gray-100 rounded-lg p-3">
                <p className="text-sm font-semibold text-navy-900">{safeStr(item.finding)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Confidence {safeStr(item.confidence)}% · {safeStr(item.evidenceState)} · Staff: {safeStr(item.staffStatus)}
                </p>
                <p className="text-sm text-gray-700 mt-2">{safeStr(item.nextDecision)}</p>
              </div>
            ))}
          </div>
        )}

        {mechanism && (
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-1">Event vs mechanism</p>
            <p className="text-sm text-gray-800">{safeStr(mechanism.event?.statement)}</p>
            <p className="text-sm text-amber-900 mt-2">{safeStr(mechanism.caution)}</p>
          </div>
        )}

        {facts.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-2">Confirmed facts</p>
            <ul className="space-y-1">
              {facts.slice(0, 6).map((f) => (
                <li key={f.id} className="text-sm text-gray-700">{safeStr(f.statement)}</li>
              ))}
            </ul>
          </div>
        )}

        {showActions && localFindings.length > 0 && (
          <div className="space-y-3 border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide">Staff confirmation</p>
            {localFindings.map((f) => (
              <div key={f.id} className="border border-gray-100 rounded-lg p-3">
                <p className="text-sm font-medium text-navy-900">{safeStr(f.title)}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {safeStr(f.evidenceState)} · {safeStr(f.interventionLevel)} · {safeStr(f.staffStatus)}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['confirmed', 'needs_more_evidence', 'rejected'].map((status) => (
                    <button
                      key={status}
                      type="button"
                      disabled={busyId === f.id}
                      onClick={() => confirmFinding(f, status)}
                      className="text-xs px-2.5 py-1.5 rounded border border-navy-200 text-navy-900 hover:bg-navy-50 disabled:opacity-50"
                    >
                      {status === 'confirmed' ? 'Confirm' : status === 'rejected' ? 'Reject' : 'Needs more evidence'}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {verification.length > 0 && (
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-2">Next-match verification</p>
            {verification.map((v) => (
              <div key={v.findingId} className="mb-3">
                <p className="text-sm font-medium text-gray-800">{safeStr(v.title)}</p>
                <ul className="mt-1 space-y-0.5">
                  {(v.questions || []).slice(0, 4).map((q, i) => (
                    <li key={i} className="text-xs text-gray-600">• {safeStr(q)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {reviewQs.length > 0 && (
          <div className="border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-2">Questions requiring review</p>
            <ul className="space-y-1">
              {reviewQs.slice(0, 8).map((q, i) => (
                <li key={i} className="text-sm text-gray-700">{safeStr(q)}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

function CoachingPlanSection({ plan }) {
  if (!plan) return null;
  const list = (arr) => Array.isArray(arr) ? arr : [];
  return (
    <Card>
      <SectionHeader icon={Target} title="Coaching Response Plan" subtitle="The response after diagnosis: what staff should do next" />
      <div className="px-6 py-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FieldBlock label="Primary Objective" value={plan.primaryObjective} />
          <FieldBlock label="Secondary Objective" value={plan.secondaryObjective} />
          <FieldBlock label="Tactical Principle" value={plan.tacticalPrinciple} />
          <FieldBlock label="Team Unit Affected" value={plan.teamUnitAffected} />
          <FieldBlock label="Suggested Session Duration" value={plan.suggestedSessionDuration} />
          <FieldBlock label="Suggested Drill Count" value={plan.suggestedDrillCount} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <FieldBlock label="Coaching Points" value={list(plan.coachingPoints).join(' · ')} />
          <FieldBlock label="Expected Improvement Indicators" value={list(plan.expectedImprovementIndicators).join(' · ')} />
          <FieldBlock label="Success Criteria Next Match" value={list(plan.successCriteriaNextMatch).join(' · ')} />
        </div>
        {list(plan.previousActionContext).length > 0 && <FieldBlock label="Previous Action Context" value={list(plan.previousActionContext).join(' · ')} />}
      </div>
    </Card>
  );
}

function TrainingSessionSection({ session }) {
  if (!session) return null;
  const blocks = ['warmUp', 'technicalDrill', 'mainTacticalDrill', 'conditionedGame', 'reviewDiscussion', 'coolDown'];
  return (
    <Card>
      <SectionHeader icon={Dumbbell} title="Training Session Plan" subtitle="A session built from the report findings" />
      <div className="px-6 py-5">
        {session.title && <p className="text-navy-900 font-bold mb-4">{safeStr(session.title)}</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {blocks.map((key) => session[key] ? (
            <div key={key} className="border border-gray-200 rounded-xl p-4">
              <p className="text-navy-900 font-bold text-sm mb-1">{safeStr(session[key].title)}</p>
              <p className="text-xs font-semibold text-gray-400 uppercase mb-1">Why it exists</p>
              <p className="text-gray-700 text-sm mb-2">{safeStr(session[key].why)}</p>
              <p className="text-gray-600 text-sm">{safeStr(session[key].detail)}</p>
            </div>
          ) : null)}
        </div>
      </div>
    </Card>
  );
}

function ExecutiveDashboardSection({ dashboard }) {
  if (!dashboard) return null;
  return (
    <Card>
      <SectionHeader icon={Activity} title="Coach Executive Dashboard" subtitle="Decision-support snapshot from current and historical report evidence" />
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <FieldBlock label="Top 5 Recurring Problems" value={Array.isArray(dashboard.topRecurringProblems) ? dashboard.topRecurringProblems.map((p) => p.issue || p.label || p).join(' · ') : null} />
        <FieldBlock label="Most Dangerous Pattern" value={dashboard.mostDangerousPattern} />
        <FieldBlock label="Most Improved Player" value={dashboard.mostImprovedPlayer || 'Not enough player history yet'} />
        <FieldBlock label="Most Improved Unit" value={dashboard.mostImprovedUnit || 'Not enough unit history yet'} />
        <FieldBlock label="Biggest Tactical Risk" value={dashboard.biggestTacticalRisk} />
        <FieldBlock label="Training Focus Next Week" value={dashboard.trainingFocusNextWeek} />
        <FieldBlock label="Top Improvements" value={Array.isArray(dashboard.topImprovements) ? dashboard.topImprovements.join(' · ') : null} />
      </div>
    </Card>
  );
}

function TeamHealthSection({ health }) {
  if (!Array.isArray(health) || health.length === 0) return null;
  return (
    <Card>
      <SectionHeader icon={Activity} title="Team Health" subtitle="Accumulated tactical health indicators from report evidence" />
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {health.map((h, i) => (
          <div key={i}>
            <div className="flex items-center justify-between text-sm mb-1"><span className="font-semibold text-navy-900">{safeStr(h.area)}</span><span className="font-bold text-navy-900">{safeStr(h.score)}</span></div>
            <div className="h-2 rounded-full bg-gray-100"><div className="h-2 rounded-full bg-navy-900" style={{ width: `${Math.max(0, Math.min(100, Number(h.score) || 0))}%` }} /></div>
            {h.basis && <p className="text-xs text-gray-400 mt-1">{safeStr(h.basis)}</p>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function IntelligenceGridSection({ title, subtitle, icon, data }) {
  if (!data) return null;
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0));
  if (entries.length === 0) return null;
  return (
    <Card>
      <SectionHeader icon={icon} title={title} subtitle={subtitle} />
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {entries.map(([k, v]) => <FieldBlock key={k} label={k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase())} value={Array.isArray(v) ? v.join(' · ') : v} />)}
      </div>
    </Card>
  );
}

function DecisionQualitySection({ items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <Card>
      <SectionHeader icon={BookOpen} title="Decision Quality" subtitle="Evidence-based review of staff decisions and match-management choices" />
      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {items.map((item, i) => (
          <div key={i} className="border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between gap-2 mb-2"><p className="font-bold text-navy-900 text-sm">{safeStr(item.area)}</p><Badge text={item.effectiveness} color={item.effectiveness === 'High' ? 'green' : item.effectiveness === 'Medium' ? 'amber' : 'red'} /></div>
            <p className="text-gray-700 text-sm">{safeStr(item.evidence)}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Next Match Risk Card
// ─────────────────────────────────────────────────────────────────────────────

function RiskCard({ r, index }) {
  if (typeof r === 'string') {
    return <div className="flex gap-2 text-sm text-gray-700 py-1"><span className="text-red-400 mt-0.5">•</span>{safeStr(r)}</div>;
  }
  const risk = pick(r, 'risk');
  const likelihood = pick(r, 'likelihood');
  const mitigation = pick(r, 'mitigation');
  const opponentContext = pick(r, 'opponentContext', 'opponent_context');
  const trainingPriority = pick(r, 'trainingPriority', 'training_priority');
  const monitorDuring = pick(r, 'monitorDuring', 'monitor_during');

  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
        <p className="text-navy-900 font-bold text-sm">{safeStr(risk) || `Risk ${index + 1}`}</p>
        {likelihood && <Badge text={`${safeStr(likelihood)} likelihood`} color={severityColor(likelihood)} />}
      </div>
      {mitigation && (
        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 mb-3">
          <p className="text-xs font-semibold text-blue-600 mb-0.5">Mitigation</p>
          <p className="text-blue-800 text-sm">{safeStr(mitigation)}</p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {opponentContext && <FieldBlock label="Opponent Context" value={opponentContext} />}
        {trainingPriority && <FieldBlock label="Training Priority" value={trainingPriority} />}
        {monitorDuring && <FieldBlock label="Monitor During Match" value={monitorDuring} className="sm:col-span-2" />}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Input Quality Score Section
// ─────────────────────────────────────────────────────────────────────────────

function InputQualitySection({ report }) {
  const iqObj = pick(report, 'input_quality', 'inputQuality');
  const iqParsed = iqObj && typeof iqObj === 'object' ? iqObj : null;
  const qualityScore = pick(report, 'input_quality_score', 'inputQualityScore') ?? (iqParsed ? iqParsed.score : null);
  const qualityLabel = pick(report, 'input_quality_label', 'inputQualityLabel') ?? (iqParsed ? iqParsed.label : null);
  const breakdown = iqParsed ? pick(iqParsed, 'breakdown') : null;
  const missingItems = iqParsed ? pick(iqParsed, 'missingItems', 'missing_items') : null;
  const note = iqParsed ? pick(iqParsed, 'note') : null;
  const evidenceCount = pick(report, 'evidence_count', 'evidenceCount');
  const missingData = pick(report, 'missing_data_warning', 'missingDataWarning') || {};
  const warnings = Array.isArray(pick(missingData, 'warnings')) ? pick(missingData, 'warnings') : [];
  const nextData = Array.isArray(pick(missingData, 'nextDataToAdd', 'next_data_to_add')) ? pick(missingData, 'nextDataToAdd', 'next_data_to_add') : [];

  if (qualityScore == null && !qualityLabel && !breakdown && !missingItems && !note && warnings.length === 0 && nextData.length === 0) return null;

  return (
    <Card>
      <SectionHeader icon={Activity} title="Evidence Quality and Next Steps" subtitle="Evidence limits, confidence, and the next data to add" />
      <div className="px-6 py-5">
        {qualityScore != null && (
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1 bg-gray-100 rounded-full h-3 overflow-hidden">
              <div
                className="h-full rounded-full transition-all bg-navy-800"
                style={{ width: `${Math.max(Number(qualityScore) || 2, 2)}%` }}
              />
            </div>
            <span className="text-navy-900 font-black text-xl tabular-nums">
              {safeStr(qualityScore)}<span className="text-gray-400 font-normal text-sm">/100</span>
            </span>
            {qualityLabel && <Badge text={qualityLabel} color={qualityColor(qualityLabel)} />}
          </div>
        )}
        {evidenceCount != null && Number(evidenceCount) > 0 && (
          <p className="text-gray-500 text-xs mb-4">
            {safeStr(evidenceCount)} key moment{Number(evidenceCount) !== 1 ? 's' : ''} with video timestamps linked
          </p>
        )}
        {breakdown && typeof breakdown === 'object' && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-2">Breakdown Checklist</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.keys(breakdown).map((key) => {
                const val = breakdown[key];
                const isYes = val === true || val === 1 || val === 'true';
                return (
                  <div key={key} className="flex items-center gap-2 text-sm">
                    {isYes
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      : <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                    <span className={isYes ? 'text-gray-700' : 'text-gray-500'}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())}
                    </span>
                    <span className={`text-xs font-medium ml-auto ${isYes ? 'text-emerald-600' : 'text-amber-500'}`}>
                      {isYes ? 'Yes' : 'No'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {missingItems && Array.isArray(missingItems) && missingItems.length > 0 && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-amber-600 uppercase tracking-wide mb-2">Missing Items</p>
            <ul className="space-y-1">
              {missingItems.map((item, i) => (
                <li key={i} className="flex gap-2 text-sm text-amber-700">
                  <span className="text-amber-400 mt-0.5">•</span>{safeStr(item)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {note && (
          <div className="bg-gray-50 rounded-lg px-4 py-3">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Evidence note</p>
            <p className="text-gray-700 text-sm leading-relaxed">{safeStr(note)}</p>
          </div>
        )}
        {warnings.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-2">Evidence limits</p>
            <ul className="space-y-1">
              {warnings.map((warning, index) => <li key={index} className="text-gray-700 text-sm">{safeStr(warning)}</li>)}
            </ul>
          </div>
        )}
        {nextData.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-2">Next data to add</p>
            <ul className="space-y-1">
              {nextData.map((item, index) => <li key={index} className="text-gray-700 text-sm">{safeStr(item)}</li>)}
            </ul>
          </div>
        )}
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main ReportView Component renders all 12 sections
// ─────────────────────────────────────────────────────────────────────────────

export default function ReportView({ match, report, externalVideoUrl, showActions, onCopy, copied }) {
  const teamName = pick(match, 'our_team_name', 'ourTeamName', 'team_name', 'teamName') || '';
  const opponent = pick(match, 'opponent_name', 'opponentName', 'opponent') || '';
  const matchDate = pick(match, 'match_date', 'matchDate') || '';
  const ourScoreVal = pick(match, 'our_score', 'ourScore');
  const oppScoreVal = pick(match, 'opponent_score', 'opponentScore');
  const finalScore = (ourScoreVal !== undefined && ourScoreVal !== null && oppScoreVal !== undefined && oppScoreVal !== null)
    ? `${ourScoreVal}-${oppScoreVal}`
    : (pick(match, 'final_score', 'finalScore') || '');
  const resultLabel = (ourScoreVal !== undefined && ourScoreVal !== null && oppScoreVal !== undefined && oppScoreVal !== null)
    ? (Number(ourScoreVal) > Number(oppScoreVal) ? 'Win' : Number(ourScoreVal) < Number(oppScoreVal) ? 'Loss' : 'Draw')
    : '';
  const tacticalFocus = pick(match, 'tactical_focus', 'tacticalFocus') || 'General review';
  const reportType = pick(match, 'report_type', 'reportType') || 'Post-Match Analysis';

  const engineFindings = pick(report, 'analysis_engine_findings', 'analysisEngineFindings') || {};
  const dmNorm = normalise(pick(report, 'decisive_moments', 'decisiveMoments', 'decisiveMomentReview'));
  let decisiveMoments = [];
  if (dmNorm.kind === 'objects') decisiveMoments = dmNorm.value;
  else if (dmNorm.kind === 'list') decisiveMoments = dmNorm.value;
  else if (dmNorm.kind === 'string') decisiveMoments = [dmNorm.value];

  const timelineNorm = normalise(pick(engineFindings, 'scoredMoments', 'matchTimeline', 'timeline'));
  let timelineMoments = [];
  if (timelineNorm.kind === 'objects') timelineMoments = timelineNorm.value;
  else if (timelineNorm.kind === 'list') timelineMoments = timelineNorm.value;
  if (timelineMoments.length === 0) timelineMoments = decisiveMoments;

  const minuteValue = (moment) => {
    const raw = safeStr(pick(moment, 'minute')).replace("'", '');
    const [base, added] = raw.split('+').map((part) => Number(part));
    if (!Number.isFinite(base)) return Number.MAX_SAFE_INTEGER;
    return base + (Number.isFinite(added) ? added / 100 : 0);
  };
  const chronologicalMoments = [...timelineMoments].sort((a, b) => minuteValue(a) - minuteValue(b));
  const sortedByImpact = [...decisiveMoments].sort((a, b) => impactScore(b) - impactScore(a));
  decisiveMoments = sortedByImpact.slice(0, 4);
  const positiveMoments = decisiveMoments.filter((m) => isPositiveMoment(m));
  const riskMoments = decisiveMoments.filter((m) => isRiskMoment(m));

  const hypNorm = normalise(pick(report, 'tactical_hypotheses', 'tacticalHypotheses', 'tacticalCauseHypotheses'));
  const hypotheses = hypNorm.kind === 'objects' ? hypNorm.value
    : hypNorm.kind === 'list' ? hypNorm.value
    : hypNorm.kind === 'string' ? [hypNorm.value] : [];

  const mistakeNorm = normalise(pick(report, 'mistake_attribution', 'mistakeAttribution'));
  const mistakes = mistakeNorm.kind === 'objects' ? mistakeNorm.value
    : mistakeNorm.kind === 'list' ? mistakeNorm.value : [];

  const coachNorm = normalise(pick(report, 'coach_decision_review', 'coachDecisionReview', 'coachingDecisionReview'));
  const coachDecisions = coachNorm.kind === 'objects' ? coachNorm.value
    : coachNorm.kind === 'list' ? coachNorm.value : [];

  const trainingNorm = normalise(pick(report, 'training_focus', 'trainingFocus', 'training_focus_blocks', 'trainingFocusBlocks'));
  let trainingItems = [];
  if (trainingNorm.kind === 'objects') trainingItems = trainingNorm.value;
  else if (trainingNorm.kind === 'list') trainingItems = trainingNorm.value;
  else if (trainingNorm.kind === 'string') trainingItems = [trainingNorm.value];

  const riskNorm = normalise(pick(report, 'next_match_risk', 'nextMatchRisk'));
  let nextRisks = [];
  if (riskNorm.kind === 'objects') nextRisks = riskNorm.value;
  else if (riskNorm.kind === 'list') nextRisks = riskNorm.value;
  else if (riskNorm.kind === 'string') nextRisks = [{ risk: riskNorm.value }];

  const riskModelNorm = normalise(pick(report, 'tactmark_risk_model', 'tactmarkRiskModel'));
  const tactmarkRiskModel = riskModelNorm.kind === 'objects' ? riskModelNorm.value
    : riskModelNorm.kind === 'list' ? riskModelNorm.value
    : riskModelNorm.kind === 'string' ? (() => { try { const parsed = JSON.parse(riskModelNorm.value); return Array.isArray(parsed) ? parsed : []; } catch { return []; } })() : [];

  const reportId = pick(report, 'id', 'report_id');
  const coachingPlan = pick(report, 'coaching_response_plan', 'coachingResponsePlan');
  const coachingIntelligence = pick(report, 'coaching_intelligence', 'coachingIntelligence')
    || pick(engineFindings, 'coachingIntelligence', 'coaching_intelligence');
  const trainingSession = pick(report, 'training_session', 'trainingSession');
  const executiveDashboard = pick(report, 'executive_dashboard', 'executiveDashboard');
  const teamHealth = pick(report, 'team_health', 'teamHealth');
  const seasonIntelligence = pick(report, 'season_intelligence', 'seasonIntelligence');
  const decisionQuality = pick(report, 'decision_quality', 'decisionQuality');
  const engineExplanation = pick(report, 'engine_explanation', 'engineExplanation');

  const [actionItems, setActionItems] = useState(trainingItems);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const handleDownloadPdf = async () => {
    if (!reportId || pdfBusy) return;
    setPdfError('');
    setPdfBusy(true);
    try {
      await api.downloadReportPdf(reportId);
    } catch (e) {
      setPdfError(e && e.message ? e.message : 'PDF export is temporarily unavailable. The report remains available online.');
    } finally {
      setPdfBusy(false);
    }
  };
  const trainingItemsKey = JSON.stringify(trainingItems);
  useEffect(() => {
    setActionItems(trainingItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trainingItemsKey]);
  const handleStatusChange = async (index, status) => {
    setActionItems((items) => items.map((item, i) => i === index ? { ...item, status } : item));
    if (showActions && reportId) {
      try { await api.updateTrainingFocusBlockStatus({ reportId, index, status }); } catch {}
    }
  };

  const rawVideoBaseUrl = externalVideoUrl
    || pick(report, 'reference_video_url', 'referenceVideoUrl')
    || pick(match, 'original_submitted_video_url', 'originalSubmittedVideoUrl', 'original_video_url', 'originalVideoUrl', 'external_video_url', 'externalVideoUrl');
  const videoBaseUrl = (() => {
    if (!rawVideoBaseUrl) return null;
    try {
      const parsed = new URL(String(rawVideoBaseUrl).trim());
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
      return parsed.toString();
    } catch {
      return null;
    }
  })();

  const verdictText = pick(report, 'verdict_summary', 'verdictSummary', 'executiveVerdict', 'executive_verdict') || '';
  const whatDecided = pick(report, 'what_decided_match', 'whatDecidedTheMatch', 'whatDecidedMatch') || '';
  const highestPositive = pick(report, 'highest_positive_moment', 'highestPositiveMoment');
  const highestRisk = pick(report, 'highest_risk_moment', 'highestRiskMoment');
  const mainTrainingPriority = pick(report, 'main_training_priority', 'mainTrainingPriority');
  const qualityLabel = pick(report, 'input_quality_label', 'inputQualityLabel')
    ?? (pick(report, 'input_quality', 'inputQuality')?.label ?? null);

  const snapshotItems = [
    { label: 'Date', value: matchDate },
    { label: 'Score', value: finalScore ? (resultLabel ? `${finalScore} (${resultLabel})` : finalScore) : '' },
    { label: 'Tactical Focus', value: tacticalFocus },
    { label: 'Report Type', value: reportType },
  ].filter((item) => safeStr(item.value));

  const hasAppendix = mistakes.length > 0 || coachDecisions.length > 0 || tactmarkRiskModel.length > 0
    || Boolean(executiveDashboard) || (Array.isArray(teamHealth) && teamHealth.length > 0)
    || Boolean(seasonIntelligence) || (Array.isArray(decisionQuality) && decisionQuality.length > 0)
    || Boolean(engineExplanation);

  return (
    <div>
      <Card>
        <SectionHeader icon={Award} title="Match Snapshot" subtitle="Verified match context and report scope" />
        <div className="px-6 py-5">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-5 pb-5 border-b border-gray-100">
            <div>
              <p className="text-navy-700 text-xs uppercase tracking-widest font-semibold mb-1">Post-Match Analysis Report</p>
              <h1 className="text-2xl font-bold leading-tight text-navy-900">
                {teamName || 'Team'} <span className="text-gray-400 font-normal">vs</span> {opponent || 'Opponent'}
              </h1>
              {finalScore && <p className="text-3xl font-bold mt-1 tracking-tight text-navy-900">{safeStr(finalScore)}</p>}
            </div>
            {qualityLabel && (
              <div className="text-right">
                <p className="text-gray-400 text-xs mb-1">Evidence quality</p>
                <span className="inline-block text-xs font-semibold px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 text-gray-700">{safeStr(qualityLabel)}</span>
              </div>
            )}
          </div>
          {snapshotItems.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {snapshotItems.map(({ label, value }) => (
                <div key={label}>
                  <p className="text-gray-400 text-xs mb-1">{label}</p>
                  <p className="text-navy-900 text-sm font-semibold leading-tight">{safeStr(value)}</p>
                </div>
              ))}
            </div>
          )}
          {(highestPositive || highestRisk || mainTrainingPriority) && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5 pt-5 border-t border-gray-100">
              {highestPositive && <FieldBlock label="Strongest Positive Response" value={highestPositive} />}
              {highestRisk && <FieldBlock label="Decisive Risk" value={highestRisk} />}
              {mainTrainingPriority && <FieldBlock label="Main Training Priority" value={mainTrainingPriority} />}
            </div>
          )}
          <p className="text-gray-400 text-xs mt-4">
            {/* Snapshot wording: positive events are not automatically decisive */}
            {(() => {
              const totalReviewed = decisiveMoments.length;
              if (totalReviewed === 0) return null;
              const posCount = positiveMoments.length;
              const riskCount = riskMoments.length;
              if (posCount > 0 && riskCount > 0) {
                return `${posCount} key positive response${posCount === 1 ? '' : 's'} and ${riskCount} decisive risk event${riskCount === 1 ? '' : 's'} reviewed.`;
              }
              if (posCount > 0) return `${posCount} key positive response${posCount === 1 ? '' : 's'} reviewed.`;
              if (riskCount > 0) return `${riskCount} decisive risk event${riskCount === 1 ? '' : 's'} reviewed.`;
              return `${totalReviewed} key moment${totalReviewed === 1 ? '' : 's'} reviewed.`;
            })()}
          </p>
        </div>
      </Card>

      {showActions && (
        <div className="flex items-center justify-end gap-2 mb-5 flex-wrap">
          <button onClick={onCopy} className="print-hidden flex items-center gap-1.5 text-sm font-medium border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 transition-colors">
            {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-500" /> : <BookOpen className="w-4 h-4 text-gray-500" />}
            {copied ? 'Copied' : 'Copy report'}
          </button>
          <button onClick={handleDownloadPdf} disabled={pdfBusy || !reportId} className="print-hidden flex items-center gap-1.5 text-sm font-semibold bg-navy-900 text-white rounded-lg px-4 py-2 hover:bg-navy-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed">
            <Download className="w-4 h-4" /> {pdfBusy ? 'Preparing PDF...' : 'Download PDF'}
          </button>
          {pdfError && <p className="print-hidden w-full text-right text-xs text-amber-600 mt-1">{pdfError}</p>}
        </div>
      )}

      {(verdictText || whatDecided) && (
        <Card>
          <SectionHeader icon={Award} title="Executive Coaching Summary" subtitle="The result, decisive score changes, and priority response" />
          <div className="px-6 py-5">
            {verdictText && <p className="text-gray-800 text-base leading-relaxed font-medium">{safeStr(verdictText)}</p>}
            {whatDecided && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-navy-600 uppercase tracking-wide mb-2">What Decided the Match</p>
                <p className="text-gray-700 text-sm leading-relaxed">{safeStr(whatDecided)}</p>
              </div>
            )}
          </div>
        </Card>
      )}

      <CoachingIntelligenceSection
        intelligence={coachingIntelligence}
        showActions={showActions}
        reportId={reportId}
      />

      {chronologicalMoments.length > 0 && (
        <Card>
          <SectionHeader icon={Clock} title="Match Timeline" subtitle="Compact score-changing event log in chronological order" />
          <div className="px-6 py-3">
            <div className="divide-y divide-gray-100">
              {chronologicalMoments.map((moment, index) => {
                const minute = pick(moment, 'minute');
                const happened = pick(moment, 'what_happened', 'whatHappened', 'event', 'moment_type', 'momentType');
                const before = pick(moment, 'score_state_before', 'scoreStateBefore');
                const after = pick(moment, 'score_state_after', 'scoreStateAfter');
                const positive = isPositiveMoment(moment);
                const risk = isRiskMoment(moment);
                return (
                  <div key={`${safeStr(minute)}-${index}`} className="grid grid-cols-[64px_1fr] gap-3 py-3 items-start">
                    <div className="font-black text-navy-900 tabular-nums">{minute ? `${safeStr(minute)}'` : `#${index + 1}`}</div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge text={positive ? 'Positive' : risk ? 'Risk' : 'Neutral'} color={positive ? 'navy' : risk ? 'red' : 'gray'} />
                        {(before || after) && <span className="text-xs text-gray-400">{safeStr(before)}{before && after ? ' to ' : ''}{safeStr(after)}</span>}
                      </div>
                      <p className="text-gray-700 text-sm mt-1 leading-relaxed">{safeStr(happened)}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {decisiveMoments.length > 0 && (
        <Card>
          <SectionHeader icon={Target} title="Decisive Moments" subtitle="Detailed moment cards appear once in this report" />
          <div className="px-6 py-5">
            {decisiveMoments.map((moment, index) => <MomentCard key={`${safeStr(pick(moment, 'minute'))}-${index}`} moment={moment} index={index} externalVideoUrl={videoBaseUrl} />)}
          </div>
        </Card>
      )}

      {hypotheses.length > 0 && (
        <Card>
          <SectionHeader icon={Lightbulb} title="Tactical Findings" subtitle="Review questions that require video confirmation before attribution" />
          <div className="px-6 py-5">
            {hypotheses.map((hypothesis, index) => <HypothesisCard key={index} h={hypothesis} index={index} externalVideoUrl={videoBaseUrl} />)}
          </div>
        </Card>
      )}

      {actionItems.length > 0 && (
        <Card>
          <SectionHeader icon={Dumbbell} title="Training Focus" subtitle="Prioritized corrective work and positive reinforcement" />
          <div className="px-6 py-5">
            {actionItems.map((item, index) => <TrainingCard key={index} t={item} index={index} showActions={showActions} onStatusChange={handleStatusChange} />)}
          </div>
        </Card>
      )}

      <CoachingPlanSection plan={coachingPlan} />
      <TrainingSessionSection session={trainingSession} />

      {nextRisks.length > 0 && (
        <Card>
          <SectionHeader icon={Shield} title="Risk Monitoring" subtitle="Risks to monitor in the next fixture without assuming the opponent" />
          <div className="px-6 py-5 space-y-3">
            {nextRisks.map((risk, index) => <RiskCard key={index} r={risk} index={index} />)}
          </div>
        </Card>
      )}

      <InputQualitySection report={report} />

      {hasAppendix && (
        <>
          <Card>
            <SectionHeader icon={BookOpen} title="Optional Appendix" subtitle="Additional evidence and internal review material" />
          </Card>
          {mistakes.length > 0 && (
            <Card>
              <SectionHeader icon={TrendingDown} title="Appendix: Mistake Attribution" subtitle="Shown only when substantive role evidence exists" />
              <div className="px-6 py-5 space-y-4">
                {mistakes.map((item, index) => <MistakeCard key={index} m={item} index={index} externalVideoUrl={videoBaseUrl} />)}
              </div>
            </Card>
          )}
          {coachDecisions.length > 0 && (
            <Card>
              <SectionHeader icon={BookOpen} title="Appendix: Coaching Decision Review" subtitle="Submitted coaching decisions and observed outcomes" />
              <div className="px-6 py-5 space-y-4">
                {coachDecisions.map((item, index) => <DecisionCard key={index} c={item} index={index} />)}
              </div>
            </Card>
          )}
          {tactmarkRiskModel.length > 0 && (
            <Card>
              <SectionHeader icon={Shield} title="Appendix: Match Risk Model" subtitle="Additional scored risk evidence" />
              <div className="px-6 py-5 space-y-3">
                {tactmarkRiskModel.map((item, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <p className="font-bold text-navy-900">{safeStr(pick(item, 'minute')) ? `${safeStr(pick(item, 'minute'))}' ` : ''}{safeStr(pick(item, 'momentType', 'moment_type'))}</p>
                      <Badge text={pick(item, 'riskLevel', 'level') || 'Risk'} color="red" />
                    </div>
                    <FieldBlock label="Event" value={pick(item, 'whatHappened', 'what_happened')} />
                    <FieldBlock label="Staff Action" value={pick(item, 'staffAction', 'staff_action')} />
                  </div>
                ))}
              </div>
            </Card>
          )}
          <ExecutiveDashboardSection dashboard={executiveDashboard} />
          <TeamHealthSection health={teamHealth} />
          <IntelligenceGridSection title="Appendix: Season Intelligence" subtitle="Historical evidence available to staff" icon={Lightbulb} data={seasonIntelligence} />
          <DecisionQualitySection items={decisionQuality} />
          <IntelligenceGridSection title="Appendix: Engine Explanation" subtitle="Evidence, confidence, and priority basis" icon={BookOpen} data={engineExplanation} />
        </>
      )}

      {videoBaseUrl && (
        <div className="bg-white rounded-2xl border border-gray-200 px-6 py-4 mb-5 flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-gray-400 text-xs uppercase tracking-wide font-semibold mb-1">Reference Footage</p>
            <a href={videoBaseUrl} target="_blank" rel="noopener noreferrer" className="text-navy-700 hover:underline text-sm break-all">
              {safeStr(pick(match, 'external_video_platform', 'externalVideoPlatform')) || 'External link'}: {safeStr(videoBaseUrl)}
            </a>
          </div>
        </div>
      )}

      <p className="text-gray-300 text-xs text-center mt-4 pb-8">
        Generated by the Tactmark Analysis Engine · {teamName} vs {opponent} · {safeStr(matchDate)}
      </p>
    </div>
  );
}
